import { toast } from "sonner"
import { useParams } from "react-router-dom"
import EditInput from "./edit-input"
import { useState } from "react"
import { ChromePicker } from "react-color"
import { useRef } from "react"
import useOutsideClick from "@/hooks/use-outside-click"
import PaletteButton from "./palette-button"
import { aptUnitSizeStore } from "../apt-unit-size-store"
import { LOCKED_SIZE } from "@/routes/admin/admin-const"

// ! useState props로 받는 이유: 렌더링 이슈(렌더의 속도 차이가 남)
const AptUnitEditPanel = ({
  dong,
  mode,
  originPalette,
  editPalette,
  unitMap,
  setEditPalette,
  setEditUnitMap
}) => {
  const { aptCode } = useParams()
  const { maxFloor, setMaxFloor } = aptUnitSizeStore()
  const { maxHo, setMaxHo, selectedSize, setSelectedSize, apiSaveAptUnitSize, setSelectedDong } =
    aptUnitSizeStore()

  const [currentColor, setCurrentColor] = useState("#fff")
  const [currentSize, setCurrentSize] = useState("")
  const [colorPickOpen, setColorPickOpen] = useState(false)
  const pickerRef = useRef()

  const [addColorMode, setAddColorMode] = useState("EDIT")

  useOutsideClick({
    ref: pickerRef,
    enabled: colorPickOpen,
    onOutside: () => setColorPickOpen(false)
  })

  const normalizeUnitMap = (unitMap, maxFloor, maxHo) => {
    const result = {}

    Object.entries(unitMap).forEach(([unitNo, size]) => {
      const num = Number(unitNo)

      const floor = Math.floor(num / 100)
      const ho = num % 100

      if (floor <= maxFloor && ho <= maxHo) {
        result[num] = size
      }
    })

    return result
  }

  const handleSave = async () => {
    const normalized = normalizeUnitMap(unitMap, maxFloor, maxHo)
    const isPaletteChanged = JSON.stringify(editPalette) !== JSON.stringify(originPalette)

    const hos = Object.entries(normalized).map(([ho, size]) => ({
      ho: Number(ho),
      size
    }))
    // console.log(aptCode, dong, hos, editPalette)
    // return
    const success = await apiSaveAptUnitSize({
      kapt_code: aptCode,
      dong,
      hos,
      palette: isPaletteChanged ? editPalette : null
    })

    if (success) {
      toast.success("저장되었습니다.")
      setSelectedDong(null)
    }
  }

  const onAddColor = () => {
    if (editPalette[currentSize]) return toast.error("이미 존재하는 평수입니다.")
    if (Object.values(editPalette).includes(currentColor))
      return toast.error("이미 사용중인 색상입니다.")
    setEditPalette((prev) => {
      return {
        ...prev,
        [currentSize]: currentColor
      }
    })
  }

  const onEditColor = () => {
    const isSizeChanged = currentSize !== selectedSize

    if (!currentSize) {
      return toast.error("평수 등록이 필요합니다.")
    }

    if (isSizeChanged && editPalette[currentSize]) {
      return toast.error("이미 존재하는 평수입니다.")
    }

    const duplicated = Object.entries(editPalette).some(
      ([size, color]) => size !== selectedSize && color === currentColor
    )

    if (duplicated) {
      return toast.error("이미 사용중인 색상입니다.")
    }

    setEditPalette((prev) => {
      const next = { ...prev }

      // 평수명이 바뀌었다면 기존 제거
      if (isSizeChanged) {
        delete next[selectedSize]
      }

      next[currentSize] = currentColor

      return next
    })

    setSelectedSize(currentSize)

    toast.success("평수 및 색상이 수정되었습니다.")
  }

  const onDeleteColor = () => {
    if (!window.confirm(`${selectedSize} 평형을 삭제하시겠습니까?`)) {
      return
    }

    // 1. palette 삭제
    setEditPalette((prev) => {
      const next = { ...prev }

      delete next[selectedSize]

      return next
    })

    // 2. 해당 평수 사용하는 호수 전부 삭제
    setEditUnitMap((prev) => {
      const next = {}

      Object.entries(prev).forEach(([unitNo, size]) => {
        if (size !== selectedSize) {
          next[unitNo] = size
        }
      })

      return next
    })

    // 3. 선택 초기화
    setSelectedSize(null)
    setCurrentSize("")
    setCurrentColor("")

    toast.success("해당 평수와 연결된 모든 호수가 삭제되었습니다.")
  }

  return (
    <section className="w-75 flex flex-col h-full border-l p-5 border-l-gray-200">
      <h2 className="font-bold text-lg mb-5">동호수 편집기</h2>

      <div className="space-y-3">
        <EditInput
          label={"동 선택"}
          onChange={(e) => setSelectedDong(Number(e.target.value))}
          disabled={mode === "EDIT"}
          value={dong}
        />

        <EditInput
          label={"최대 층수"}
          onChange={(e) => setMaxFloor(Number(e.target.value))}
          value={maxFloor}
        />

        <EditInput
          label={"최대 호수"}
          onChange={(e) => setMaxHo(Number(e.target.value))}
          value={maxHo}
        />
      </div>

      <hr className="my-6 text-gray-200" />

      <article className="space-y-2">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold">팔레트 선택</h3>
        </div>

        <div className="flex flex-col gap-2 mb-6 pb-2 border-b border-b-gray-200">
          <EditInput
            type="text"
            label={"평수 입력"}
            onChange={(e) => setCurrentSize(e.target.value)}
            value={currentSize}
          />
          <EditInput label="색상 선택">
            <div className="relative">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setColorPickOpen(true)}
                  className="w-16 border border-gray-300 h-8 rounded-sm cursor-pointer hover:scale-105 transition-all"
                  style={{ backgroundColor: currentColor }}
                  disabled={currentSize === ""}
                />
                <p className="flex-1 border border-gray-300 text-sm rounded-sm h-8 flex items-center px-3 select-none">
                  {currentColor}
                </p>
              </div>
              {colorPickOpen && (
                <div className="absolute" ref={pickerRef}>
                  <ChromePicker
                    onChange={(c) => {
                      setCurrentColor(c.hex)
                    }}
                    color={currentColor}
                  />
                </div>
              )}
            </div>
          </EditInput>

          <div className="flex items-center justify-end gap-2 mt-2">
            {addColorMode === "CREATE" ? (
              <button className="bg-gray-400 rounded-sm px-2 py-0.5" onClick={onAddColor}>
                등록
              </button>
            ) : (
              <button className="bg-gray-400 rounded-sm px-2 py-0.5" onClick={onEditColor}>
                변경
              </button>
            )}
            <button
              className="bg-red-500 rounded-sm px-2 py-0.5 text-gray-50"
              onClick={onDeleteColor}
            >
              삭제
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 overflow-y-auto">
          <PaletteButton
            label={"비우기"}
            onClick={() => {
              setSelectedSize(null)
              setCurrentColor("")
              setCurrentSize("")
            }}
            isActive={selectedSize === null}
          />

          <PaletteButton
            label={"필로티"}
            onClick={() => {
              setSelectedSize(LOCKED_SIZE)
              setCurrentColor("")
              setCurrentSize("")
            }}
            isActive={selectedSize === LOCKED_SIZE}
          />

          {Object.entries(editPalette).map(([size, color]) => (
            <PaletteButton
              key={size}
              label={size}
              onClick={() => {
                setCurrentColor(color)
                setCurrentSize(size)
                setSelectedSize(size)
                setAddColorMode("EDIT")
              }}
              isActive={selectedSize === size}
              boxColor={color}
            />
          ))}

          <PaletteButton
            label={"추가"}
            onClick={() => {
              setSelectedSize("CREATE")
              setAddColorMode("CREATE")
            }}
            isActive={selectedSize === "CREATE"}
          />
        </div>
      </article>

      <div className="mt-auto flex gap-2">
        <button
          className="flex-1 border border-gray-400 text-gray-600 py-2 rounded hover:bg-gray-100 transition-all cursor-pointer"
          onClick={() => setSelectedDong(null)}
        >
          취소
        </button>

        <button className="flex-1 bg-blue-600 text-white py-2 rounded" onClick={handleSave}>
          저장
        </button>
      </div>
    </section>
  )
}

export default AptUnitEditPanel
